import {DataTestAttrs} from '@benchling/js-test-utils';
import {Tooltip, createStyles} from '@mantine/core';
import {useId} from '@mantine/hooks';
import React from 'react';

import Button, {ButtonProps, ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';

interface ExportLatestVisibleVersionButtonProps {
  buttonText: string;
  className?: string;
  dataTestAttrs?: DataTestAttrs;
  disabledTooltip: string | null;
  onClick: NonNullable<ButtonProps['onClick']>;
}

/**
 * Export control for a versioned item, which can only produce the latest version the viewer can see. The
 * reason it is unavailable shows as a tooltip on hover and is linked to the button through
 * `aria-describedby`, so screen readers announce it on focus rather than only that the button is disabled.
 */
export default function ExportLatestVisibleVersionButton({
  buttonText,
  className,
  dataTestAttrs,
  disabledTooltip,
  onClick,
}: ExportLatestVisibleVersionButtonProps): JSX.Element {
  const {classes} = useStyles();
  const disabledReasonId = useId();
  const isDisabled = disabledTooltip != null;
  return (
    <>
      <Tooltip
        label={disabledTooltip ?? ''}
        disabled={!isDisabled}
        multiline={true}
        width={240}
        // Keep the tooltip outside the panel so overflow clipping does not hide it.
        withinPortal={true}
      >
        {/* ui-library Button is not a forwardRef component, so Tooltip needs a block wrapper. */}
        <div>
          <Button
            aria-describedby={isDisabled ? disabledReasonId : undefined}
            aria-disabled={isDisabled || undefined}
            buttonStyle={ButtonStyle.TERTIARY_GRAY}
            className={className}
            dataTestAttrs={dataTestAttrs}
            disabled={isDisabled}
            fullWidth={true}
            onClick={onClick}
            size={ButtonSize.SMALL}
            text={buttonText}
          />
        </div>
      </Tooltip>
      {/* The tooltip only mounts while hovered, and targets the wrapper rather than the button, so the
          reason lives here to stay reachable from the button itself. */}
      {isDisabled && (
        <span className={classes.visuallyHidden} id={disabledReasonId}>
          {disabledTooltip}
        </span>
      )}
    </>
  );
}

const useStyles = createStyles(() => ({
  visuallyHidden: {
    border: 0,
    clip: 'rect(0 0 0 0)',
    height: 1,
    margin: -1,
    overflow: 'hidden',
    padding: 0,
    position: 'absolute',
    whiteSpace: 'nowrap',
    width: 1,
  },
}));
